import { Weld, WorkPackage, PersonaType, MaterialCommodity } from '@bechtel/types';
import { filter, flow, some, toLower } from 'lodash';
import { FormikValues } from 'formik';
import { useName } from '@bechtel/client-common';

type CommodityTypes = MaterialCommodity | Weld;

type PotentialEntities = CommodityTypes | WorkPackage;

const filterColectionByPropValue = (prop: string, value: string) => (collection: PotentialEntities[]) => {
  return filter(collection, (entity) => {
    return entity[prop] === value;
  });
};

const entityContainsPropValue = (prop: string, value: string) => (entity: PotentialEntities) => {
  // NOTE: When comparing strings and characters, includes() is case-sensitive.
  const propLowered = toLower(entity[prop]);
  const valueLowered = toLower(value);
  return propLowered.includes(valueLowered);
};

const collectionContainsPropValue = (prop: string, value: string) => (collection: PotentialEntities[]) => {
  return some(collection, entityContainsPropValue(prop, value));
};

const searchByOwner = (persona: PersonaType, value: string) => (entity: PotentialEntities) => {
  const { getFullName } = useName();
  const { primaryOwners: assignedCrewMembers } = entity as WorkPackage;
  if (!assignedCrewMembers || assignedCrewMembers.length === 0) return false;

  const valueLowered = toLower(value);

  return assignedCrewMembers
    .filter((c) => c.persona === persona)
    .some((c) => {
      if (!c.crewMember) return false;

      // NOTE: String.includes is case sensitive
      const crewMemberNameLowered = toLower(getFullName(c.crewMember.firstName, c.crewMember.lastName));
      return crewMemberNameLowered.includes(valueLowered);
    });
};

const searchSpools = (searchQuery: string) => {
  return flow([filterColectionByPropValue('type', 'Spool'), collectionContainsPropValue('tag', searchQuery)]);
};

const searchWelds = (searchQuery: string) => {
  return flow([filterColectionByPropValue('type', 'Weld'), collectionContainsPropValue('tag', searchQuery)]);
};

const searchBulkPipes = (searchQuery: string) => {
  return flow([filterColectionByPropValue('type', 'BulkPipe'), collectionContainsPropValue('tag', searchQuery)]);
};

export const searchByFn = (searchByKey: FormikValues['searchBy'], searchQuery: string) => (
  workPackage: WorkPackage
) => {
  if (!searchQuery) return true;

  if (searchByKey === 'WORK_PACKAGE') {
    return entityContainsPropValue('tag', searchQuery)(workPackage);
  }

  if (searchByKey === 'SPOOL') {
    return searchSpools(searchQuery)(workPackage.commodities);
  }

  if (searchByKey === 'BULK_PIPE') {
    return searchBulkPipes(searchQuery)(workPackage.commodities);
  }

  if (searchByKey === 'WELD') {
    return searchWelds(searchQuery)(workPackage.commodities);
  }

  if (searchByKey === 'FOREMAN') {
    return searchByOwner(PersonaType.FOREMAN, searchQuery)(workPackage);
  }

  if (searchByKey === 'GENERAL_FOREMAN') {
    return searchByOwner(PersonaType.GENERAL_FOREMAN, searchQuery)(workPackage);
  }

  if (searchByKey === 'SUPERINTENDENT') {
    return searchByOwner(PersonaType.SUPERINTENDENT, searchQuery)(workPackage);
  }

  if (searchByKey === 'FIELD_ENGINEER') {
    return searchByOwner(PersonaType.FIELD_ENGINEER, searchQuery)(workPackage);
  }
  if (searchByKey === 'FIELD_WELD_ENGINEER') {
    return searchByOwner(PersonaType.FIELD_WELD_ENGINEER, searchQuery)(workPackage);
  }
};

// TODO: wire this up once we are know what these filters mean
export const filterByFn = (filterByKeys: FormikValues['filterBy']) => (workPackage: WorkPackage) => {
  if (filterByKeys === 'CURRENT') {
    return workPackage.startDate;
  }

  if (filterByKeys === 'APPROVAL') {
    return workPackage.endDate;
  }

  return workPackage.id;
};

export const sortByFn = (sortByKey: FormikValues['searchBy']) => (workPackage: WorkPackage) => {
  if (sortByKey === 'WORK_PACKAGE') {
    return workPackage.tag;
  }

  if (sortByKey === 'START_DATE') {
    return workPackage.startDate;
  }

  if (sortByKey === 'END_DATE') {
    return workPackage.endDate;
  }

  if (sortByKey === 'PERCENT_COMPLETE') {
    return workPackage.earnedHours / workPackage.totalEarnableHours;
  }
};
